import { Component, Inject, InjectionToken, OnDestroy, OnInit, } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription, } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { FacetModalContentComponent } from 'search/components/facet-modal-content/facet-modal-content.component';
import { IterableFacetData } from 'search/facets/models/facet-data';
import { FacetSchemaProps } from 'search/facets/models/facet-schema';
import { FacetState, makeFacetState, } from 'search/facets/models/facet-state';
import { Facet, FacetResult, } from 'search/facets/models/resource-facet';
import { ComponentData } from 'search/models/filter-panel';
import { FacetModalContext } from 'search/models/modal-window-data';

export const FACET_COMPONENT_DATA = new InjectionToken<ComponentData>('FACET_COMPONENT_DATA');

type FacetWithSelection = (Facet & { selected?: boolean })[];

@Component({
  selector: 'app-facet',
  templateUrl: './facet.component.html',
  styleUrls: ['./facet.component.scss'],
})
export class FacetComponent implements OnInit, OnDestroy {
  public AMOUNT_FOR_DISPLAY_ON_FACE_PANEL = 5;

  public facetKey: string;
  public subscriptions = new Subscription();
  public facetsToShow: FacetWithSelection;
  public isShowMore: boolean;
  public selectedFacets: Facet[] = [];
  public facets: FacetResult<Facet>;
  public facetsLeftCount: number;
  public isLoading: Subject<boolean> = new Subject().pipe(debounceTime(300)) as Subject<boolean>;
  public isShowAllAvailable: boolean;
  public facetName: string;
  public blockName: string;
  public facetPluralName: string;
  public expanded: boolean;
  public facetSchema: FacetSchemaProps;
  public facetId: string;

  constructor(
    @Inject(FACET_COMPONENT_DATA) public componentData: ComponentData,
    private modalService: NgbModal
  ) {
  }

  public ngOnInit() {
    // @ts-ignore
    this.subscriptions.add(this.componentData.facetData.subscribe((facetState: FacetState<Facet[], IterableFacetData> = makeFacetState()) => {
      this.isLoading.next(facetState.isLoading);
      this.facets = facetState.schemaWithData.data;
      this.selectedFacets = facetState.selected;
      if (!facetState.expanded) {
        this.isShowMore = false;
      }
      this.expanded = facetState.expanded;
      this.facetsToShow = this.calculateDisplayingFacetsToShow(this.facets, this.selectedFacets);
      const facetSchema = facetState.schemaWithData.schema;
      this.isShowAllAvailable = facetSchema.hasModal;
      this.facetName = facetSchema.blockName.toLowerCase();
      this.blockName = facetSchema.blockName;
      this.facetPluralName = facetSchema.pluralName;
      this.facetSchema = facetSchema;
      this.facetId = facetSchema.mapField;
    }));

    this.facetKey = this.componentData.facetKey;
  }

  public onShowMore() {
    this.isShowMore = true;
    this.facetsToShow = this.calculateDisplayingFacetsToShow(this.facets, this.selectedFacets);
  }

  public onShowAll() {
    this.openDialog();
  }

  public onShowFewer() {
    this.isShowMore = false;
    this.facetsToShow = this.calculateDisplayingFacetsToShow(this.facets, this.selectedFacets);
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public trackById(index: number, facet: Facet): string {
    return facet.id;
  }

  public openDialog(): void {
    const modalRef = this.modalService.open(FacetModalContentComponent,
      {
        backdrop: 'static',
        windowClass: 'facet-modal',
      });
    modalRef.componentInstance.context = {
      facetResult: this.facets,
      facetSchema: this.facetSchema,
      selectedFacets: this.selectedFacets,
    } as FacetModalContext;
  }

  private calculateDisplayingFacetsToShow(existingFacets: FacetResult<Facet>,
                                          selectedFacets: FacetWithSelection): FacetWithSelection {
    const existing = existingFacets.data;
    existing.forEach((f) => f.selected = false);
    selectedFacets.forEach((selectedFacet) => {
      const found = existing.find((facet) => facet.id === selectedFacet.id);
      selectedFacet.selected = true;
      if (found) {
        found.selected = true;
        found.isResetLocked = selectedFacet.isResetLocked;
      }
    });
    const facetsToShow = existing.slice(0, this.isShowMore ? existingFacets.totalResults : this.AMOUNT_FOR_DISPLAY_ON_FACE_PANEL);
    this.facetsLeftCount = existingFacets.totalResults - facetsToShow.length;
    return facetsToShow;
  }
}
